/**
 * Where a scanned card lands: a card in a CSA box says "scan for the wreath
 * preorder," and this is what the QR code points at (/p/:code). Two very
 * different visitors can land here — whoever's holding the card might not
 * be signed in yet, or might not be a member at all — so this checks the
 * unauthenticated preview first (services/api/src/routes/public.ts,
 * GET /public/v1/p/:code) and only calls the member-only detail route once
 * a session exists. Payment never happens here — see member.ts's comment on
 * the preorder routes for why: a preorder is a commitment, not a charge.
 */

import { useEffect, useState } from 'preact/hooks';
import { Link } from 'wouter-preact';
import type { JSX } from 'preact';

import { apiDelete, apiGet, apiPatch, apiPost, ApiError, getSession } from '../api.js';

const WAITLIST_URL = 'https://flockandfurrow.farm/csa';

interface PublicPreview {
  found: boolean;
  product_name?: string;
  description?: string | null;
  fulfillment_route?: 'PREORDER' | 'IN_BOX' | 'ADD_ON';
  state?: string;
  price_minor?: string | null;
}

interface Variant {
  id: string;
  name: string;
}

interface OfferChoice {
  product_variant_id: string;
  rank: number;
}

interface OfferLine {
  id: string;
  quantity: string;
  choices: OfferChoice[];
}

interface ExistingOrder {
  id: string;
  state: string;
  lines: OfferLine[];
}

interface OfferDetail {
  offer: {
    id: string;
    product_name: string;
    description: string | null;
    fulfillment_route: 'PREORDER' | 'IN_BOX' | 'ADD_ON';
    state: string;
    price_minor: string | null;
    preorder_closes_at: string | null;
    pickup_window_start: string | null;
    pickup_window_end: string | null;
    allow_ranked_choice: boolean;
    can_order: boolean;
  };
  variants: Variant[];
  existing_order: ExistingOrder | null;
}

function money(minor: string | null | undefined): string {
  if (minor == null) return 'Free';
  return `$${(Number(minor) / 100).toFixed(2)}`;
}

function formatDate(iso: string | null): string | null {
  if (!iso) return null;
  return new Date(iso).toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' });
}

/** Rank pickers for offers that take multiple ranked choices — specialty pumpkins, not a plain quantity. */
function RankedChoicePicker({
  variants,
  ranks,
  onChange,
}: {
  variants: Variant[];
  ranks: Record<string, number | ''>;
  onChange: (variantId: string, rank: number | '') => void;
}): JSX.Element {
  return (
    <div class="stack" style="gap: 0.5rem;">
      {variants.map((variant) => (
        <label key={variant.id} class="check" style="justify-content: space-between;">
          <span>{variant.name}</span>
          <select
            value={ranks[variant.id] ?? ''}
            onChange={(event) => {
              const raw = (event.target as HTMLSelectElement).value;
              onChange(variant.id, raw ? Number(raw) : '');
            }}
            style="width: auto; min-height: 2.4rem;"
          >
            <option value="">Not interested</option>
            {variants.map((_, index) => (
              <option key={index} value={index + 1}>
                {index === 0 ? '1st choice' : index === 1 ? '2nd choice' : index === 2 ? '3rd choice' : `${index + 1}th choice`}
              </option>
            ))}
          </select>
        </label>
      ))}
    </div>
  );
}

function PreorderForm({
  code,
  detail,
  onDone,
}: {
  code: string;
  detail: OfferDetail;
  onDone: () => void;
}): JSX.Element {
  const { offer, variants } = detail;
  const [quantity, setQuantity] = useState(1);
  const [variantId, setVariantId] = useState<string>(variants[0]?.id ?? '');
  const [ranks, setRanks] = useState<Record<string, number | ''>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function choicesPayload(): OfferChoice[] | undefined {
    if (variants.length === 0) return undefined;
    if (offer.allow_ranked_choice) {
      return Object.entries(ranks)
        .filter(([, rank]) => rank !== '')
        .map(([product_variant_id, rank]) => ({ product_variant_id, rank: rank as number }));
    }
    return variantId ? [{ product_variant_id: variantId, rank: 1 }] : undefined;
  }

  async function submit(event: Event): Promise<void> {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const choices = choicesPayload();
      if (offer.allow_ranked_choice && (!choices || choices.length === 0)) {
        setError('Rank at least one choice.');
        setBusy(false);
        return;
      }
      await apiPost('/member/v1/products/orders', { short_code: code, quantity, choices });
      onDone();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save that. Try again in a moment.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form class="card" onSubmit={(event) => void submit(event)}>
      <span class="card-title">Preorder</span>

      {variants.length > 0 && offer.allow_ranked_choice && (
        <>
          <span class="card-sub">Rank your choices — the farm allocates by preference once the preorder closes.</span>
          <RankedChoicePicker variants={variants} ranks={ranks} onChange={(id, rank) => setRanks((r) => ({ ...r, [id]: rank }))} />
        </>
      )}

      {variants.length > 0 && !offer.allow_ranked_choice && (
        <label>
          Choose
          <select value={variantId} onChange={(event) => setVariantId((event.target as HTMLSelectElement).value)}>
            {variants.map((variant) => (
              <option key={variant.id} value={variant.id}>
                {variant.name}
              </option>
            ))}
          </select>
        </label>
      )}

      <label>
        Quantity
        <input
          type="number"
          min="1"
          inputMode="numeric"
          value={quantity}
          onInput={(event) => setQuantity(Math.max(1, Number((event.target as HTMLInputElement).value) || 1))}
          required
        />
      </label>

      {error && <p class="error" role="alert">{error}</p>}
      <button class="primary" type="submit" disabled={busy}>
        {busy ? 'Saving…' : 'Preorder'}
      </button>
      <span class="card-sub small muted">
        No payment now — the farm will send a Shopify payment link once the preorder closes and the count is final.
      </span>
    </form>
  );
}

function ExistingOrderCard({
  detail,
  onChanged,
}: {
  detail: OfferDetail;
  onChanged: () => void;
}): JSX.Element {
  const { offer, existing_order, variants } = detail;
  if (!existing_order) throw new Error('ExistingOrderCard requires an existing order');
  const order: ExistingOrder = existing_order;
  const [quantity, setQuantity] = useState(Number(order.lines[0]?.quantity ?? 1));
  const [busy, setBusy] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canEdit = order.state === 'COMMITTED' && offer.can_order;

  function variantName(id: string): string {
    return variants.find((v) => v.id === id)?.name ?? id;
  }

  async function saveQuantity(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await apiPatch(`/member/v1/products/orders/${order.id}`, { quantity });
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save that. Try again in a moment.');
    } finally {
      setBusy(false);
    }
  }

  async function cancel(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await apiDelete(`/member/v1/products/orders/${order.id}`);
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not cancel that. Try again in a moment.');
      setBusy(false);
    }
  }

  return (
    <section class="card">
      <span class="card-title">Your preorder</span>
      <span class="card-sub">
        {order.state === 'COMMITTED' ? 'Committed' : order.state.charAt(0) + order.state.slice(1).toLowerCase()}
        {order.lines[0] && ` · ${order.lines[0].quantity}`}
      </span>
      {order.lines[0]?.choices.map((choice) => (
        <span key={choice.product_variant_id} class="card-sub small">
          #{choice.rank} {variantName(choice.product_variant_id)}
        </span>
      ))}

      {!canEdit && (
        <span class="card-sub small muted">
          {order.state === 'COMMITTED' ? 'This preorder has closed — contact the farm to change it.' : 'No longer editable.'}
        </span>
      )}

      {canEdit && !cancelling && (
        <>
          <label>
            Quantity
            <input
              type="number"
              min="1"
              inputMode="numeric"
              value={quantity}
              onInput={(event) => setQuantity(Math.max(1, Number((event.target as HTMLInputElement).value) || 1))}
            />
          </label>
          {error && <p class="error" role="alert">{error}</p>}
          <div style="display: flex; gap: 0.75rem; flex-wrap: wrap;">
            <button class="primary" disabled={busy} onClick={() => void saveQuantity()}>
              {busy ? 'Saving…' : 'Update quantity'}
            </button>
            <button class="link danger" disabled={busy} onClick={() => setCancelling(true)}>
              Cancel preorder
            </button>
          </div>
        </>
      )}

      {canEdit && cancelling && (
        <div class="card card-danger">
          <span class="card-title">Cancel this preorder?</span>
          <span class="card-sub">Free to undo — you can preorder again while it's still open.</span>
          {error && <p class="error" role="alert">{error}</p>}
          <div style="display: flex; gap: 0.75rem; flex-wrap: wrap;">
            <button class="link danger" disabled={busy} onClick={() => void cancel()}>
              {busy ? 'Working…' : 'Yes, cancel'}
            </button>
            <button class="link" onClick={() => setCancelling(false)}>
              Never mind
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

export function OfferView({ code }: { code: string }): JSX.Element {
  const signedIn = Boolean(getSession());
  const [preview, setPreview] = useState<PublicPreview | null>(null);
  const [detail, setDetail] = useState<OfferDetail | null>(null);
  const [notActive, setNotActive] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  function load(): void {
    apiGet<PublicPreview>(`/public/v1/p/${code}`)
      .then(setPreview)
      .catch(() => setPreview({ found: false }));

    if (signedIn) {
      apiGet<OfferDetail>(`/member/v1/products/offers/${code}`)
        .then((result) => {
          setDetail(result);
          setNotActive(false);
        })
        .catch((err) => {
          if (err instanceof ApiError && err.status === 403) {
            setNotActive(true);
          } else if (err instanceof ApiError && err.status === 404) {
            // Already covered by the public preview's found:false case.
          } else {
            setLoadError(err instanceof ApiError ? err.message : 'Could not load this offer.');
          }
        });
    }
  }

  useEffect(load, [code, signedIn]);

  if (preview === null) {
    return (
      <main class="view centred">
        <p class="muted" role="status">Opening…</p>
      </main>
    );
  }

  if (!preview.found) {
    return (
      <main class="view centred">
        <h1>Not found</h1>
        <p class="muted">That code doesn't match anything currently on offer.</p>
        <Link href="/" class="primary">
          Back to your account
        </Link>
      </main>
    );
  }

  const notPreorder = preview.fulfillment_route !== 'PREORDER';

  return (
    <main class="view reveal offer-view">
      <header>
        <span class="ff-eyebrow">From our farm</span>
        <h1>{preview.product_name}</h1>
        {preview.description && <p class="muted">{preview.description}</p>}
      </header>

      {notPreorder && (
        <section class="card">
          <span class="card-title">No need to preorder</span>
          <span class="card-sub">
            {preview.fulfillment_route === 'IN_BOX'
              ? "This shows up in a box automatically when there's enough — nothing to do here."
              : 'This is added on to a box directly by the farm — nothing to do here.'}
          </span>
          {preview.price_minor != null && <span class="card-sub">{money(preview.price_minor)}</span>}
        </section>
      )}

      {!notPreorder && !signedIn && (
        <section class="card card-accent">
          <span class="card-title">{money(preview.price_minor)}</span>
          <span class="card-sub">Sign in as a CSA member to preorder.</span>
          <Link href={`/login?next=/p/${code}`} class="primary">
            Sign in
          </Link>
          <span class="card-sub small muted">
            Not a member yet? <a href={WAITLIST_URL}>Join the waitlist</a>.
          </span>
        </section>
      )}

      {!notPreorder && signedIn && notActive && (
        <section class="card">
          <span class="card-title">Preorders are for active CSA members</span>
          <span class="card-sub">Your account isn't an active membership right now.</span>
          <a class="primary" href={WAITLIST_URL}>
            Join the waitlist
          </a>
        </section>
      )}

      {!notPreorder && signedIn && loadError && <p class="error" role="alert">{loadError}</p>}

      {!notPreorder && signedIn && detail && (
        <>
          <section class="card">
            <span class="card-title">{money(detail.offer.price_minor)}</span>
            {detail.offer.preorder_closes_at && (
              <span class="card-sub">Preorders close {formatDate(detail.offer.preorder_closes_at)}.</span>
            )}
            {detail.offer.pickup_window_start && (
              <span class="card-sub">
                Pickup {formatDate(detail.offer.pickup_window_start)}
                {detail.offer.pickup_window_end && detail.offer.pickup_window_end !== detail.offer.pickup_window_start
                  ? ` – ${formatDate(detail.offer.pickup_window_end)}`
                  : ''}
              </span>
            )}
            {!detail.offer.can_order && !detail.existing_order && (
              <span class="card-sub muted">This preorder isn't open right now.</span>
            )}
          </section>

          {detail.existing_order && <ExistingOrderCard detail={detail} onChanged={load} />}

          {!detail.existing_order && detail.offer.can_order && <PreorderForm code={code} detail={detail} onDone={load} />}
        </>
      )}

      <Link href="/account" class="link">
        ← Your account
      </Link>
    </main>
  );
}
